import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Search, Heart, LayoutGrid, Loader2, SearchX, ListOrdered, PaintBucket, Cat, Boxes, BookA, Sparkles, Languages, Lightbulb, Car, Palette } from 'lucide-react';
import BottomNavBar from '../components/BottomNavBar';
import GameCard, { GameType, CategoryType, AgeGroupType } from '../components/GameCard';
import SEOMeta from '../components/SEOMeta';

const ALL_ID = 'semua';

// Map Material icon names to Lucide components
const ICON_MAP: Record<string, React.FC<any>> = {
  grid_view: LayoutGrid,
  calculate: ListOrdered,
  palette: PaintBucket,
  pets: Cat,
  shapes: Boxes,
  abc: BookA,
  auto_awesome: Sparkles,
  language: Languages,
  lightbulb: Lightbulb,
  directions_car: Car,
  draw: Palette,
};

/* ── Styled ── */

const PageWrapper = styled.div`
  max-width: 512px;
  margin: 0 auto;
  min-height: 100dvh;
  background: #f6f8f8;
  padding-bottom: 96px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

/* Search */
const SearchSection = styled.div`
  padding: 20px 16px 0;
  h2 {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 14px 0;
    font-family: inherit;
  }
`;

const SearchWrapper = styled.label`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 14px;
  color: #2DBCAF;
  display: flex;
  svg { width: 22px; height: 22px; }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 16px 0 46px;
  background: white;
  border: 2px solid rgba(45,188,175,0.15);
  border-radius: 14px;
  font-size: 15px;
  font-family: inherit;
  color: #0f172a;
  outline: none;
  transition: border 0.2s, box-shadow 0.2s;
  &::placeholder { color: #94a3b8; }
  &:focus {
    border-color: #2DBCAF;
    box-shadow: 0 0 0 4px rgba(45,188,175,0.1);
  }
`;

/* Age Filter */
const FilterLabelRow = styled.div`
  padding: 14px 16px 6px;
  font-size: 11px;
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-family: inherit;
`;

const AgeScroll = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 16px 4px;
  overflow-x: auto;
  &::-webkit-scrollbar { display: none; }
`;

const AgePill = styled.button<{ active?: boolean }>`
  flex-shrink: 0;
  padding: 8px 18px;
  border-radius: 9999px;
  border: 2px solid ${p => p.active ? '#2DBCAF' : '#e2e8f0'};
  background: ${p => p.active ? '#2DBCAF' : 'white'};
  color: ${p => p.active ? 'white' : '#64748b'};
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
`;

/* Category Filter */
const CategoryScroll = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 16px 16px;
  overflow-x: auto;
  &::-webkit-scrollbar { display: none; }
`;

const CategoryPillBtn = styled.button<{ active?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 9999px;
  border: 2px solid ${p => p.active ? '#2DBCAF' : '#e2e8f0'};
  background: ${p => p.active ? '#2DBCAF' : 'white'};
  color: ${p => p.active ? 'white' : '#64748b'};
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  svg { width: 16px; height: 16px; }
`;

const FavoritesBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(239,68,68,0.08);
  border: 1.5px solid rgba(239,68,68,0.18);
  border-radius: 9999px;
  padding: 6px 14px;
  cursor: pointer;
  color: #ef4444;
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  flex-shrink: 0;
  svg { width: 16px; height: 16px; }
`;

/* Game Grid */
const GameGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 0 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: #94a3b8;
  svg { width: 48px; height: 48px; display: block; margin: 0 auto 12px; }
  p { font-size: 14px; font-weight: 600; margin: 0; font-family: inherit; }
`;

/* ── Component ── */

export default function GamesPage() {
  const navigate = useNavigate();
  const [games, setGames]           = useState<GameType[]>([]);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [ageGroups, setAgeGroups]   = useState<AgeGroupType[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [activeCatId, setActiveCatId]   = useState(ALL_ID);
  const [activeAgeId, setActiveAgeId]   = useState(ALL_ID);

  useEffect(() => {
    Promise.all([
      fetch('/api/games').then(r => r.json()),
      fetch('/api/categories').then(r => r.json()),
      fetch('/api/age-groups').then(r => r.json()),
    ])
      .then(([g, c, a]: [GameType[], CategoryType[], AgeGroupType[]]) => {
        setGames(g);
        setCategories(c.filter((x: CategoryType) => x.slug !== 'semua'));
        setAgeGroups(a);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = games.filter(g => {
    const matchSearch = g.name.toLowerCase().includes(search.toLowerCase());
    const matchCat    = activeCatId === ALL_ID || g.categories.some(c => c._id === activeCatId);
    const matchAge    = activeAgeId === ALL_ID || g.ageGroups.some(a => a._id === activeAgeId);
    return matchSearch && matchCat && matchAge;
  });

  const getCategoryIcon = (iconName: string) => {
    const IconComponent = ICON_MAP[iconName] || LayoutGrid;
    return <IconComponent />;
  };

  return (
    <>
      <SEOMeta
        title="Semua Game Edukasi Anak"
        description="Temukan ratusan game edukasi interaktif untuk anak usia 3-6 tahun. Filter berdasarkan usia dan kategori: huruf, angka, hewan, warna, logika."
        path="/games"
      />
      <PageWrapper>
        {/* Search */}
        <SearchSection>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ margin: 0 }}>What shall we play today?</h2>
            <FavoritesBtn onClick={() => navigate('/favorites')}>
              <Heart fill="currentColor" />
              Favorit
            </FavoritesBtn>
          </div>
          <SearchWrapper>
            <SearchIconWrap>
              <Search />
            </SearchIconWrap>
            <SearchInput
              placeholder="Cari game edukatif..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </SearchSection>

        {/* Age Filter */}
        <FilterLabelRow>Pilih Usia</FilterLabelRow>
        <AgeScroll>
          <AgePill active={activeAgeId === ALL_ID} onClick={() => setActiveAgeId(ALL_ID)}>Semua</AgePill>
          {ageGroups.map(ag => (
            <AgePill key={ag._id} active={activeAgeId === ag._id} onClick={() => setActiveAgeId(ag._id)}>
              {ag.name}
            </AgePill>
          ))}
        </AgeScroll>

        {/* Category Filter */}
        <CategoryScroll>
          <CategoryPillBtn active={activeCatId === ALL_ID} onClick={() => setActiveCatId(ALL_ID)}>
            <LayoutGrid />
            All
          </CategoryPillBtn>
          {categories.map(cat => (
            <CategoryPillBtn key={cat._id} active={activeCatId === cat._id} onClick={() => setActiveCatId(cat._id)}>
              {getCategoryIcon(cat.icon)}
              {cat.name}
            </CategoryPillBtn>
          ))}
        </CategoryScroll>

        {/* Game Grid */}
        {loading ? (
          <EmptyState>
            <Loader2 className="animate-spin" />
            <p>Memuat game...</p>
          </EmptyState>
        ) : filtered.length === 0 ? (
          <EmptyState>
            <SearchX />
            <p>Game tidak ditemukan</p>
          </EmptyState>
        ) : (
          <GameGrid>
            {filtered.map(game => (
              <GameCard key={game._id} game={game} />
            ))}
          </GameGrid>
        )}
      </PageWrapper>

      <BottomNavBar />
    </>
  );
}
